import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';
import { formatDisplayDate } from '../../utils/formatDate';

const CustomerFavoritesTable = ({
    endpoint = `${config.API_BASE_URL}/api/actions/business/favorites`,
    title = 'Customer engagement',
    subtitle = 'Customers who saved your venues or content in the mobile app.',
    isAdmin = false,
    hideContact = false
}) => {
    const { auth } = useAuth();
    const [data, setData] = useState({
        summary: {
            total_venue_favorites: 0,
            total_blog_favorites: 0,
            total_favorites: 0,
            unique_customers: 0
        },
        favorites: [],
        total: 0
    });
    const [activeType, setActiveType] = useState('all');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchFavorites = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams({ limit: '50' });
            if (activeType !== 'all') params.append('type', activeType);
            if (search.trim()) params.append('search', search.trim());

            const response = await auth.fetch(`${endpoint}?${params.toString()}`);
            if (!response.ok) throw new Error('Failed to load favorites');
            setData(await response.json());
        } catch (err) {
            console.error('Error fetching favorites table:', err);
            setError(err.message || 'Unable to load favorites');
        } finally {
            setLoading(false);
        }
    }, [activeType, auth, endpoint, search]);

    useEffect(() => {
        const timer = setTimeout(fetchFavorites, 300);
        return () => clearTimeout(timer);
    }, [fetchFavorites]);

    const formatDate = (dateValue) => {
        if (!dateValue) return '—';
        const d = new Date(dateValue);
        if (Number.isNaN(d.getTime())) return '—';
        const datePart = formatDisplayDate(d);
        const timePart = d.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return `${datePart}, ${timePart}`;
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').filter(Boolean).map((part) => part[0]).slice(0, 2).join('').toUpperCase();
    };

    const summaryItems = [
        { label: 'Total Saves', value: data.summary.total_favorites || 0, icon: 'solar:heart-linear', tone: 'rose' },
        { label: 'Venue Saves', value: data.summary.total_venue_favorites || 0, icon: 'solar:shop-2-linear', tone: 'blue' },
        { label: 'Content Saves', value: data.summary.total_blog_favorites || 0, icon: 'solar:document-text-linear', tone: 'amber' },
        { label: 'Unique Customers', value: data.summary.unique_customers || data.summary.unique_users_count || 0, icon: 'solar:users-group-rounded-linear', tone: 'green' }
    ];

    return (
        <section className="hs-modern-card bo-engagement" aria-labelledby="engagement-title">
            <header className="bo-engagement-header">
                <div className="bo-engagement-title">
                    <div className="bo-icon bo-icon-rose" aria-hidden="true"><Icon icon="solar:heart-bold" /></div>
                    <div>
                        <h3 id="engagement-title">{title}</h3>
                        {subtitle && <p>{subtitle}</p>}
                    </div>
                </div>

                <div className="bo-engagement-tools">
                    <label className="bo-search">
                        <span className="visually-hidden">Search customer engagement</span>
                        <Icon icon="solar:magnifer-linear" aria-hidden="true" />
                        <input type="search" placeholder="Search customers or items" value={search} onChange={(event) => setSearch(event.target.value)} />
                        {search && (
                            <button type="button" onClick={() => setSearch('')} aria-label="Clear search"><Icon icon="solar:close-circle-linear" /></button>
                        )}
                    </label>

                    <div className="bo-segmented" role="group" aria-label="Favorite type">
                        <button type="button" className={activeType === 'all' ? 'is-active' : ''} onClick={() => setActiveType('all')}>All</button>
                        <button type="button" className={activeType === 'venue' ? 'is-active' : ''} onClick={() => setActiveType('venue')}>Venues</button>
                        <button type="button" className={activeType === 'blog' ? 'is-active' : ''} onClick={() => setActiveType('blog')}>Content</button>
                    </div>

                    <button type="button" className="bo-refresh" onClick={fetchFavorites} disabled={loading} aria-label="Refresh customer engagement">
                        <Icon icon="solar:restart-linear" className={loading ? 'spin' : ''} />
                    </button>
                </div>
            </header>

            <div className="bo-engagement-summary">
                {summaryItems.map((item) => (
                    <div className="bo-summary-item" key={item.label}>
                        <div className={`bo-icon bo-icon-${item.tone}`} aria-hidden="true"><Icon icon={item.icon} /></div>
                        <div><span>{item.label}</span><strong>{item.value}</strong></div>
                    </div>
                ))}
            </div>

            {loading ? (
                <LoadingState message="Loading customer engagement..." />
            ) : error ? (
                <div className="p-4 text-center text-danger text-sm" role="alert">
                    <Icon icon="solar:danger-triangle-linear" className="me-1" /> {error}
                </div>
            ) : data.favorites.length === 0 ? (
                <EmptyState
                    icon="solar:heart-broken-linear"
                    title={search ? 'No matching activity' : 'No customer saves yet'}
                    description={search ? 'Try another customer or item name.' : 'Customer saves will appear here as people engage with your venues and content.'}
                />
            ) : (
                <div className="bo-table-scroll">
                    <table className="bo-table">
                        <thead>
                            <tr>
                                <th>Customer</th>
                                {!hideContact && <th>Contact</th>}
                                <th>Saved Item</th>
                                {isAdmin && <th>Venue / Owner</th>}
                                <th>Date Saved</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.favorites.map((favorite) => (
                                <tr key={`${favorite.type}-${favorite.id}`}>
                                    <td>
                                        <div className="bo-venue-name">
                                            <span className="bo-avatar">{getInitials(favorite.user_name)}</span>
                                            <div><strong>{favorite.user_name || 'Customer'}</strong><small>Customer #{favorite.user_id}</small></div>
                                        </div>
                                    </td>
                                    {!hideContact && (
                                        <td>
                                            <div><span className="bo-cell-muted">{favorite.user_email || 'No email provided'}</span>{favorite.user_phone && <small className="d-block bo-cell-muted mt-1">{favorite.user_phone}</small>}</div>
                                        </td>
                                    )}
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="bo-item-type"><Icon icon={favorite.type === 'venue' ? 'solar:shop-2-linear' : 'solar:document-text-linear'} /> {favorite.type === 'venue' ? 'Venue' : 'Content'}</span>
                                            <strong className="text-sm text-truncate" style={{ maxWidth: '280px' }} title={favorite.item_title}>{favorite.item_title}</strong>
                                        </div>
                                    </td>
                                    {isAdmin && <td><strong className="text-sm" title={favorite.venue_name}>{favorite.venue_name || '—'}</strong><small className="d-block bo-cell-muted">Owner: {favorite.venue_owner_name || 'N/A'}</small></td>}
                                    <td><span className="bo-cell-muted">{formatDate(favorite.created_at)}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default CustomerFavoritesTable;
